import React, {useState, useEffect, useContext, useCallback, useRef} from 'react';
import SurveyQuestion from './SurveyQuestion';
import CodeableContext from 'contexts/CodeableContext';
import CodeOperationsContext from 'contexts/CodeOperationsContext';
import useCopyHandler from 'hooks/useCopyHandler';
import queryString from 'query-string';
import SurveyPageWrapper from './SurveyPageWrapper';
import useHighlightAnswer from './useHighlightAnswer';
import {getAnswerSelect} from './surveySelectUtils';
import SurveyResponseNotFound from './SurveyResponseNotFound';
import { 
  useSurveyResponse, 
  useSurveyQuestions, 
  useSurveyResponseExcerpts,
  useInitializeSurveyExcerptMappings
} from 'hooks/surveys/queries';

import {
  surveyUrlOptionFilter
} from 'utils/surveyUrlEncoder';


const SurveyResponsePage = ({
  history,
  match,
  projectID,
  hasEditPermission,
  location
}) => {
  const { handleMouseDown, isAnswerDisabled } = useHighlightAnswer();
  const [notFound, setNotFound] = useState(false);

  const {
    selectedExcerptId,
    select,
    onDeselect,
    onSelectText
  } = useContext(CodeableContext);
  
  const {
    onExcerptSelected,
    getSubstring
  } = useContext(CodeOperationsContext);

  useCopyHandler(select, getSubstring);

  const surveyResponseID = parseInt(match.params.surveyResponseID);

  // Use react-query to fetch the survey response
  const { 
    data: surveyRow = {}, 
    isError: isSurveyRowError, 
    error: surveyRowError,
    isLoading: isLoadingSurveyRow
  } = useSurveyResponse(surveyResponseID);
  
  // Use react-query to fetch survey questions
  const {
    data: surveyQuestionsData,
    isLoading: isLoadingSurveyQuestions
  } = useSurveyQuestions(surveyRow.survey_id);

  // Handle 404/403 errors
  useEffect(() => {
    if (isSurveyRowError) {
      const status = surveyRowError?.status || surveyRowError?.response?.status;
      if (status === 404 || status === 403) {
        setNotFound(true);
      }
      console.log(surveyRowError);
    }
  }, [isSurveyRowError, surveyRowError]);

  const onGotoQuestion = useCallback((questionId) => {
    history.push(`/survey_questions/${questionId}`);
  }, [history]);

  /********\
   * 
   * Scrolling
   * 
  \********/
  const parsed = queryString.parse(location.search);
  const { snippet } = parsed;

  // Fetch excerpts when response ID changes using React Query
  // Handle snippet selection after excerpts are loaded
  useEffect(() => {
    // Only proceed if we have a valid snippet or we need to deselect
    if (snippet) {
      onExcerptSelected(snippet);
    } else {
      onDeselect();
    }
  }, [snippet, onExcerptSelected, onDeselect]);


  const scrollbarRef = useRef();
  const [hasScrolled, setHasScrolled] = useState(false);
  const questionRefs = useRef({});

  // Reset scroll position when snippet changes
  useEffect(() => {
    if (snippet) {
      setHasScrolled(false);
    }
  }, [snippet]);

  useEffect(() => {
    const selectedRef = questionRefs.current[select?.codeableId];
    
    // Check if both selectedRef and scrollbarRef are ready
    if (selectedRef && scrollbarRef.current && !hasScrolled && snippet) {
      // Using requestAnimationFrame to ensure scrolling happens post-render
      setHasScrolled(true);
      requestAnimationFrame(() => {
        scrollbarRef.current.scrollTop(selectedRef.offsetTop);
      });
    }
  }, [surveyRow, select?.codeableId, snippet, hasScrolled]);  // Including only dependencies that matter

  /********\
   * 
   * End of Scrolling
   * 
  \********/
  
  const onOptionClicked = useCallback((question_id, option_id) => {
    history.push(surveyUrlOptionFilter(surveyRow.survey_id, question_id, option_id));
  }, [history, surveyRow?.survey_id]);



  // Getting the excerpts for the survey response when the survey response ID changes and when the code changes

  /*
    {
      "id":1,
      "response_text":"Alex",
      "created_at":"2024-09-09T13:00:48.536Z",
      "updated_at":"2024-09-09T13:01:03.338Z",
      "survey_questions":[
          {
            "id":1,
            "question_text":"Tell us about your Delve experience",
            "question_type":"text",
            "survey_answer":{
                "id":1,
                "created_at":"2024-09-09T13:03:12.197Z",
                "updated_at":"2024-09-09T13:03:12.197Z",
                "body":"I really enjoyed it"
            }
          },
          {
            "id":2,
            "question_text":"Location",
            "question_type":"single_choice",
            "survey_answer":{
                "id":2,
                "created_at":"2024-09-09T13:15:34.889Z",
                "updated_at":"2024-09-09T13:15:34.889Z",
                "survey_option":{
                  "id":1,
                  "option_text":"Ericeira"
                }
            }
          }
      ]
    }
  */
  
  // Make sure we have survey answers to prevent errors
  const surveyAnswers = surveyRow.survey_answers || [];
  
  // Initialize paragraph excerpt mappings for survey answers
  // This hook must be called before any conditional returns
  useInitializeSurveyExcerptMappings(surveyResponseID, surveyAnswers);
  
  if (notFound) {
    return <SurveyResponseNotFound />;
  }

  return (
    <SurveyPageWrapper
      projectID={projectID}
      hasEditPermission={hasEditPermission}
      scrollbarRef={scrollbarRef}
      title={surveyRow.response_text || 'Untitled'}
      isLoading={isLoadingSurveyRow || isLoadingSurveyQuestions }
    >
      {
        surveyQuestionsData?.survey_questions?.map((surveyQuestion) => {
          const surveyAnswer = surveyAnswers.find(answer => answer.survey_question_id === surveyQuestion.id);
          return (
            <div
              key={surveyQuestion.id}
              ref={(el) => (questionRefs.current[surveyAnswer?.id] = el)}
            >
              <SurveyQuestion
                responseId={surveyResponseID}
                key={surveyQuestion.id}
                questionText={surveyQuestion?.question_text}
                questionType={surveyQuestion?.question_type}
                questionId={surveyQuestion?.id}
                surveyAnswer={surveyAnswer}
                selectedExcerptId={selectedExcerptId}
                select={getAnswerSelect(select, surveyAnswer?.id)}
                onMouseDown={handleMouseDown}
                disabled={isAnswerDisabled(surveyAnswer?.id)}
                onExcerptSelected={onExcerptSelected}
                onDeselect={onDeselect}
                onGotoQuestion={onGotoQuestion}
                onOptionClicked={onOptionClicked}
                onSelectText={onSelectText}
                hasEditPermission={hasEditPermission}
              />
            </div>
          )
        })
      }
    </SurveyPageWrapper>
  );
};

export default SurveyResponsePage;